import React, { useEffect, useRef, useState } from "react";
import { ImageTools } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Trash2, Wand2, Copy, Check, Loader2, Undo2, Square, Hexagon, CornerDownLeft } from "lucide-react";

const ImageEditor = () => {
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  const [sourceUrl, setSourceUrl] = useState("");
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
  const [displaySize, setDisplaySize] = useState({ w: 0, h: 0 });

  const [tool, setTool] = useState("polygon");
  const [regions, setRegions] = useState([]);
  const [polygons, setPolygons] = useState([]);
  const [currentPoly, setCurrentPoly] = useState([]);
  const [rectDrag, setRectDrag] = useState(null);
  const [cursor, setCursor] = useState(null);

  const [mode, setMode] = useState("erase");
  const [feather, setFeather] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [resultUrl, setResultUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const imgRef = useRef(null);
  const containerRef = useRef(null);

  const absoluteUrl = (u) => {
    if (!u) return "";
    if (u.startsWith("http://") || u.startsWith("https://")) return u;
    if (u.startsWith("/")) return `${backend}${u}`;
    return u;
  };

  useEffect(() => {
    const onResize = () => {
      if (imgRef.current) setDisplaySize({ w: imgRef.current.clientWidth, h: imgRef.current.clientHeight });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const dispToImg = (pt) => {
    if (!displaySize.w || !naturalSize.w) return { x: 0, y: 0 };
    return { x: Math.round(pt.x * naturalSize.w / displaySize.w), y: Math.round(pt.y * naturalSize.h / displaySize.h) };
  };
  const imgToDisp = (pt) => {
    if (!displaySize.w || !naturalSize.w) return { x: 0, y: 0 };
    return { x: pt.x * displaySize.w / naturalSize.w, y: pt.y * displaySize.h / naturalSize.h };
  };
  const scaleRectFromImg = ({ x, y, w, h }) => {
    const tl = imgToDisp({ x, y });
    const br = imgToDisp({ x: x + w, y: y + h });
    return { x: tl.x, y: tl.y, w: br.x - tl.x, h: br.y - tl.y };
  };

  const commitPolygon = () => {
    if (currentPoly.length < 3) { toast.error("Need at least 3 points"); return; }
    setPolygons((prev) => [...prev, currentPoly.map(dispToImg)]);
    setCurrentPoly([]);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { setCurrentPoly([]); setRectDrag(null); }
      if (e.key === "Enter" && currentPoly.length >= 3) commitPolygon();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
     
  }, [currentPoly]);

  const onImgLoad = () => {
    if (imgRef.current) {
      setNaturalSize({ w: imgRef.current.naturalWidth, h: imgRef.current.naturalHeight });
      setDisplaySize({ w: imgRef.current.clientWidth, h: imgRef.current.clientHeight });
    }
  };

  const canvasPoint = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onCanvasClick = (e) => {
    if (!sourceUrl || !naturalSize.w || tool !== "polygon") return;
    const p = canvasPoint(e);
    if (currentPoly.length >= 3) {
      const first = currentPoly[0];
      const dx = p.x - first.x, dy = p.y - first.y;
      if (Math.sqrt(dx * dx + dy * dy) < 12) { commitPolygon(); return; }
    }
    setCurrentPoly((prev) => [...prev, p]);
  };
  const onCanvasDoubleClick = () => {
    if (tool === "polygon" && currentPoly.length >= 3) commitPolygon();
  };

  const onMouseDown = (e) => {
    if (!sourceUrl || !naturalSize.w || tool !== "rect") return;
    const p = canvasPoint(e);
    setRectDrag({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
  };
  const onMouseMove = (e) => {
    const p = canvasPoint(e);
    setCursor(p);
    if (rectDrag) setRectDrag((d) => ({ ...d, x1: p.x, y1: p.y }));
  };
  const onMouseUp = () => {
    if (!rectDrag) return;
    const x = Math.min(rectDrag.x0, rectDrag.x1);
    const y = Math.min(rectDrag.y0, rectDrag.y1);
    const w = Math.abs(rectDrag.x1 - rectDrag.x0);
    const h = Math.abs(rectDrag.y1 - rectDrag.y0);
    setRectDrag(null);
    if (w < 6 || h < 6) return;
    const tl = dispToImg({ x, y });
    const br = dispToImg({ x: x + w, y: y + h });
    setRegions((prev) => [...prev, { x: tl.x, y: tl.y, w: br.x - tl.x, h: br.y - tl.y }]);
  };

  const removeRegion = (idx) => setRegions((prev) => prev.filter((_, i) => i !== idx));
  const removePolygon = (idx) => setPolygons((prev) => prev.filter((_, i) => i !== idx));
  const clearAll = () => { setRegions([]); setPolygons([]); setCurrentPoly([]); };

  const runProcess = async () => {
    if (!sourceUrl) { toast.error("Load an image first"); return; }
    if (regions.length === 0 && polygons.length === 0) { toast.error("Draw at least one shape"); return; }
    setProcessing(true); setResultUrl("");
    try {
      const res = await ImageTools.erase({
        source_url: absoluteUrl(sourceUrl),
        regions,
        polygons,
        mode,
        feather: Number(feather) || 0,
      });
      setResultUrl(res.url);
      toast.success("Image processed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Processing failed");
    } finally { setProcessing(false); }
  };

  const copyResult = async () => {
    if (!resultUrl) return;
    const full = absoluteUrl(resultUrl);
    try { await navigator.clipboard.writeText(full); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { window.prompt("Copy this URL:", full); }
  };

  const shapeCount = regions.length + polygons.length;
  const shapeColor = mode === "erase" ? "rgb(251, 113, 133)" : "rgb(52, 211, 153)";
  const fillColor = mode === "erase" ? "rgba(244, 63, 94, 0.25)" : "rgba(16, 185, 129, 0.25)";
  const polyPts = (poly) => poly.map(imgToDisp).map((p) => `${p.x},${p.y}`).join(" ");

  return (
    <div className="p-10" data-testid="image-editor-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.22em] text-atc-blue mb-2">Tools</div>
        <h1 className="font-display font-black text-4xl text-white">Image Editor</h1>
        <p className="text-slate-400 text-sm mt-2 max-w-2xl">
          Draw <strong>rectangles</strong> or <strong>polygons</strong> to mark areas. Choose whether to <strong>erase</strong> them or <strong>keep only</strong> them.
          Polygon: click points, then double-click, press <kbd className="px-1 py-0.5 bg-slate-800 rounded border border-white/10 text-[10px]">Enter</kbd>, or click the first point to close. <kbd className="px-1 py-0.5 bg-slate-800 rounded border border-white/10 text-[10px]">Esc</kbd> cancels.
        </p>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
        <div className="glass rounded-2xl p-5">
          <div className="mb-3">
            <label className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-1.5 block">Load image</label>
            <div className="grid sm:grid-cols-[minmax(0,1fr)_auto] gap-2 items-start">
              <Input value={sourceUrl} onChange={(e) => { setSourceUrl(e.target.value); clearAll(); setResultUrl(""); }}
                placeholder="Paste any image URL (e.g. /api/files/abc123 or https://...)"
                className="bg-slate-950 border-white/10" data-testid="image-editor-url" />
              <ImageUpload value="" onChange={(url) => { setSourceUrl(url); clearAll(); setResultUrl(""); }} testId="image-editor-upload" label="…or upload" />
            </div>
          </div>

          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="text-xs uppercase tracking-[0.18em] text-slate-400 mr-2">Tool:</span>
            <button onClick={() => { setTool("polygon"); setRectDrag(null); }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border ${tool === "polygon" ? "bg-atc-blue/20 border-atc-blue/50 text-white" : "bg-slate-900 border-white/10 text-slate-400 hover:text-white"}`}
              data-testid="tool-polygon"><Hexagon className="h-3.5 w-3.5" /> Polygon</button>
            <button onClick={() => { setTool("rect"); setCurrentPoly([]); }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border ${tool === "rect" ? "bg-atc-blue/20 border-atc-blue/50 text-white" : "bg-slate-900 border-white/10 text-slate-400 hover:text-white"}`}
              data-testid="tool-rect"><Square className="h-3.5 w-3.5" /> Rectangle</button>
            {tool === "polygon" && currentPoly.length >= 3 && (
              <button onClick={commitPolygon} className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-atc-green/20 border border-atc-green/50 text-emerald-200" data-testid="commit-polygon">
                <CornerDownLeft className="h-3.5 w-3.5" /> Close shape ({currentPoly.length})
              </button>
            )}
          </div>

          {sourceUrl ? (
            <div ref={containerRef}
              className="relative w-full bg-slate-950/60 rounded-xl overflow-hidden select-none cursor-crosshair"
              onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
              onClick={onCanvasClick} onDoubleClick={onCanvasDoubleClick} data-testid="image-editor-canvas">
              <img ref={imgRef} src={absoluteUrl(sourceUrl)} onLoad={onImgLoad} alt="Source"
                className="block w-full h-auto pointer-events-none" draggable={false} />

              {displaySize.w > 0 && (
                <svg className="absolute inset-0 w-full h-full pointer-events-none"
                  viewBox={`0 0 ${displaySize.w} ${displaySize.h}`} preserveAspectRatio="none">
                  {polygons.map((poly, i) => (
                    <polygon key={`p${i}`} points={polyPts(poly)} fill={fillColor} stroke={shapeColor} strokeWidth={2} />
                  ))}
                  {regions.map((r, i) => {
                    const d = scaleRectFromImg(r);
                    return <rect key={`r${i}`} x={d.x} y={d.y} width={d.w} height={d.h} fill={fillColor} stroke={shapeColor} strokeWidth={2} />;
                  })}
                  {currentPoly.length > 0 && (
                    <>
                      <polyline
                        points={currentPoly.map((p) => `${p.x},${p.y}`).concat(cursor ? [`${cursor.x},${cursor.y}`] : []).join(" ")}
                        fill="none" stroke={shapeColor} strokeWidth={2} strokeDasharray="4 4" />
                      {currentPoly.map((p, i) => (
                        <circle key={i} cx={p.x} cy={p.y} r={5} fill={i === 0 ? "#fff" : shapeColor}
                          stroke={shapeColor} strokeWidth={2} />
                      ))}
                    </>
                  )}
                  {rectDrag && (
                    <rect
                      x={Math.min(rectDrag.x0, rectDrag.x1)} y={Math.min(rectDrag.y0, rectDrag.y1)}
                      width={Math.abs(rectDrag.x1 - rectDrag.x0)} height={Math.abs(rectDrag.y1 - rectDrag.y0)}
                      fill={fillColor} stroke={shapeColor} strokeWidth={2} strokeDasharray="4 4" />
                  )}
                </svg>
              )}

              <div className="absolute inset-0 pointer-events-none">
                {polygons.map((poly, i) => {
                  const first = imgToDisp(poly[0]);
                  return (
                    <button key={`pb${i}`} onClick={(e) => { e.stopPropagation(); removePolygon(i); }}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="absolute pointer-events-auto h-5 w-5 rounded-full bg-slate-950 border border-white/20 text-white flex items-center justify-center text-[10px] hover:bg-rose-500 -translate-x-1/2 -translate-y-1/2"
                      style={{ left: first.x, top: first.y }} data-testid={`remove-polygon-${i}`}>×</button>
                  );
                })}
                {regions.map((r, i) => {
                  const d = scaleRectFromImg(r);
                  return (
                    <button key={`rb${i}`} onClick={(e) => { e.stopPropagation(); removeRegion(i); }}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="absolute pointer-events-auto h-5 w-5 rounded-full bg-slate-950 border border-white/20 text-white flex items-center justify-center text-[10px] hover:bg-rose-500"
                      style={{ left: d.x + d.w - 10, top: d.y - 10 }} data-testid={`remove-region-${i}`}>×</button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-white/10 p-16 text-center text-slate-500 text-sm">
              Load an image above to start editing.
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="glass rounded-2xl p-5 space-y-3">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Mode</div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setMode("erase")}
                className={`px-3 py-2 rounded-lg text-xs font-semibold transition ${mode === "erase" ? "bg-rose-500/20 text-rose-200 border border-rose-500/50" : "bg-slate-900 text-slate-400 border border-white/10 hover:text-white"}`}
                data-testid="mode-erase">Erase inside</button>
              <button onClick={() => setMode("keep")}
                className={`px-3 py-2 rounded-lg text-xs font-semibold transition ${mode === "keep" ? "bg-atc-green/20 text-emerald-200 border border-atc-green/50" : "bg-slate-900 text-slate-400 border border-white/10 hover:text-white"}`}
                data-testid="mode-keep">Keep only inside</button>
            </div>
          </div>

          <div className="glass rounded-2xl p-5 space-y-3">
            <label className="text-xs uppercase tracking-[0.18em] text-slate-400 flex items-center justify-between">
              Feather edges <span className="text-slate-300 font-mono">{feather}px</span>
            </label>
            <input type="range" min={0} max={40} value={feather} onChange={(e) => setFeather(Number(e.target.value))}
              className="w-full accent-atc-blue" data-testid="feather-slider" />
          </div>

          <div className="glass rounded-2xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Shapes ({shapeCount})</div>
              {shapeCount > 0 && (
                <button onClick={clearAll} className="text-[11px] text-slate-400 hover:text-white flex items-center gap-1" data-testid="clear-shapes">
                  <Trash2 className="h-3 w-3" /> Clear all
                </button>
              )}
            </div>
            {shapeCount === 0 ? (
              <div className="text-xs text-slate-500">
                {tool === "polygon" ? "Click points on the image; double-click to close." : "Drag on the image to add a rectangle."}
              </div>
            ) : (
              <ul className="space-y-1 text-xs">
                {polygons.map((p, i) => (
                  <li key={`p${i}`} className="flex items-center gap-2 text-slate-400">
                    <Hexagon className="h-3 w-3 text-atc-blue shrink-0" /> Polygon #{i + 1} · {p.length} pts
                    <button onClick={() => removePolygon(i)} className="ml-auto text-slate-500 hover:text-rose-400"><Trash2 className="h-3 w-3" /></button>
                  </li>
                ))}
                {regions.map((r, i) => (
                  <li key={`r${i}`} className="flex items-center gap-2 text-slate-400">
                    <Square className="h-3 w-3 text-atc-blue shrink-0" /> Rect #{i + 1} · {r.w}×{r.h}px
                    <button onClick={() => removeRegion(i)} className="ml-auto text-slate-500 hover:text-rose-400"><Trash2 className="h-3 w-3" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button onClick={runProcess} disabled={processing || shapeCount === 0 || !sourceUrl}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full brand-gradient text-white text-sm font-semibold hover:opacity-95 disabled:opacity-40"
            data-testid="image-editor-process">
            {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            {processing ? "Processing…" : "Process image"}
          </button>

          {resultUrl && (
            <div className="glass rounded-2xl p-5 space-y-3" data-testid="image-editor-result">
              <div className="text-xs uppercase tracking-[0.18em] text-atc-green">Result</div>
              <div className="relative w-full rounded-lg overflow-hidden"
                style={{
                  backgroundColor: "#020617",
                  backgroundImage: "linear-gradient(45deg, #1e293b 25%, transparent 25%), linear-gradient(-45deg, #1e293b 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #1e293b 75%), linear-gradient(-45deg, transparent 75%, #1e293b 75%)",
                  backgroundSize: "16px 16px",
                  backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0px",
                }}>
                <img src={absoluteUrl(resultUrl)} alt="Result" className="block w-full h-auto" />
              </div>
              <Input value={absoluteUrl(resultUrl)} readOnly className="bg-slate-950 border-white/10 text-xs font-mono" data-testid="image-editor-result-url" />
              <div className="grid grid-cols-2 gap-2">
                <button onClick={copyResult} className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-white/15 text-xs text-white hover:bg-white/5" data-testid="image-editor-copy">
                  {copied ? <><Check className="h-3.5 w-3.5 text-atc-green" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy URL</>}
                </button>
                <button onClick={() => { setSourceUrl(resultUrl); clearAll(); setResultUrl(""); }}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-white/15 text-xs text-white hover:bg-white/5"
                  data-testid="image-editor-use-as-source">
                  <Undo2 className="h-3.5 w-3.5" /> Edit this
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageEditor;
